/*
 * One id per BROWSER, random, kept in localStorage — so one person's journey reads as one line
 * across visits (the owner's journey log, 2026-10-04): the ad on Monday, the email link on Tuesday.
 * Not a fingerprint: nothing about the device goes into it, clearing site data ends it, and a
 * private window has its own. It is linked to an account only by the server, when an event
 * arrives signed in (infra/lambda/handlers/events.ts) — the first being right after the code.
 *
 * Its own module (2026-10-14) because two read it: the funnel (analytics/funnel.ts), which sends
 * it, and the A/B test (analytics/experiment.ts), which puts the visitor in a group by it.
 */
const VISITOR_KEY = 'saddle.visitor';
const SHAPE = /^[A-Za-z0-9-]{8,64}$/;
let visitorId: string | null = null;

function randomId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
}

export function currentVisitorId(): string {
  if (visitorId) return visitorId;
  try {
    const kept = localStorage.getItem(VISITOR_KEY);
    if (kept && SHAPE.test(kept)) return (visitorId = kept);
    /*
     * Arrived from an app's own browser by "Open in Safari/Chrome" (platform/openInBrowser.ts):
     * the same person, so the same id — adopted only by a browser that has none of its own.
     */
    const carried = new URLSearchParams(typeof location !== 'undefined' ? location.search : '').get('vid');
    visitorId = carried && SHAPE.test(carried) ? carried : randomId();
    localStorage.setItem(VISITOR_KEY, visitorId);
  } catch {
    // Storage blocked: an id for this page load stands in, as the funnel's session id does.
    visitorId = visitorId ?? randomId();
  }
  return visitorId;
}

/** Test-only: the next read goes to storage afresh, as a new page load's would. */
export function _resetVisitorForTest(): void {
  visitorId = null;
}
